import React, { useCallback, useMemo, useState } from 'react';
import { useUploadManager } from '@design-stack-vista/upload-components';
import { AssetStatusType } from '@design-stack-vista/vista-assets-sdk';
import { Button, Card, FlexBox, Spinner, Typography } from '@vp/swan';
import { abortControllerManager } from 'src/managers/AbortControllerManager';
import { text } from 'src/utils/localizationUtil';
import { formatBytes } from 'src/utils/uploadUtil';
import { useFileUpload } from './useFileUpload';

interface UploadStatusProps {
  isUploading: boolean;
  isValidating: boolean;
  fileSize: number;
}

export const UploadStatus = ({ isValidating, isUploading, fileSize }: UploadStatusProps) => {
  const { assetStore } = useUploadManager();
  const { resetUploader } = useFileUpload();
  const [uploadedSize, setUploadedSize] = useState(formatBytes(0));

  // The first asset in the assetStore.assets array is the one that is currently being uploaded
  const asset = assetStore?.assets[0];

  const uploadProgress = useMemo(() => {
    if (asset?.status.type === AssetStatusType.Preparing) {
      const uploadedSizeStatus = (Number(asset?.status?.uploadProgress) * fileSize) / 100;
      setUploadedSize(formatBytes(uploadedSizeStatus));
      return asset?.status.uploadProgress !== undefined ? Number(asset.status.uploadProgress) : 0;
    }
    if (asset?.status.type === AssetStatusType.Prepared) {
      return 100;
    }
    return 0;
  }, [asset?.status, fileSize]);

  const onCancelClick = useCallback(() => {
    abortControllerManager.abort();
    resetUploader();
  }, [resetUploader]);

  return (
    <Card bordered>
      <FlexBox justifyContent="center" gap="4" mb={4}>
        <Spinner accessibleText={text('loading')} size="standard" />
        {isValidating && <Typography fontSkin="body-small-bold">{text('processing-upload')}</Typography>}
        {isUploading && (
          <Typography fontSkin="body-small-bold">
            {text('uploading-with-percentage', { percentage: uploadProgress })}
          </Typography>
        )}
      </FlexBox>
      {isUploading && (
        <Typography fontSize="xsmall" textColor="subtle" textAlign="center">
          {text('uploading-size-progress', {
            uploaded: uploadedSize,
            total: formatBytes(fileSize),
          })}
        </Typography>
      )}
      <Typography fontSize={'xsmall'} textColor="subtle" textAlign="center" mb={3}>
        {text('processing-secondary')}
      </Typography>
      {(isValidating || isUploading) && (
        <Button width="full-width" compactMode onClick={onCancelClick}>
          {text('cancel-upload-label')}
        </Button>
      )}
    </Card>
  );
};
